import { logError } from 'lib/sentry/logError';
import { VStack } from 'shared';

import { ChallengeTimelineItemType } from 'features/challenge/ChallengeTimelineItem';
import {
  GoalBreakdownChallengeCardMenu,
  GoalChallengeCard,
  IGoal,
  useGoalBreakdown,
  useGoalQueryActions,
  useMoveGoalBreakdownChallenges,
  useMoveGoalBreakdownChallengesMutation,
} from 'features/goal';

import { Droppable } from 'shared/components/DragNDrop/Droppable';
import { Sortable } from 'shared/components/DragNDrop/Sortable';
import { showErrorToast } from 'shared/components/Toast';

export const GoalBreakdownBoardChallenges = () => {
  const { challenges } = useGoalBreakdown();

  const {
    cancelGoalQueries,
    getGoalQuery,
    updateGoalQueryData,
    invalidateGoalQuery,
  } = useGoalQueryActions();

  const { mutate: mutateChallenges } = useMoveGoalBreakdownChallengesMutation({
    options: {
      onMutate: async () => {
        await cancelGoalQueries();

        const previousValue = getGoalQuery();

        return { previousValue };
      },
      onError: async (
        error: any,
        __: any,
        context: { previousValue: IGoal } | undefined
      ) => {
        if (!context?.previousValue) {
          return;
        }

        updateGoalQueryData((_: IGoal) => context.previousValue);

        showErrorToast('Failed to move challenge');
        logError(error);
      },
      onSettled: () => {
        invalidateGoalQuery();
      },
    },
  });

  const { moveToFocusArea, moveToChallenges } =
    useMoveGoalBreakdownChallenges();

  return (
    <Droppable
      acceptType={['challenge']}
      onDrop={({ id, index }) => {
        mutateChallenges({
          customChallengeId: id,
          index,
        });
      }}
      onHover={(draggedItem: {
        id: string;
        focusAreaIndex: number;
        index: number;
      }) => {
        moveToFocusArea(-1)(draggedItem);
      }}
    >
      <VStack gap="var(--spacing01)" alignItems="stretch">
        {challenges.map(
          (challenge: ChallengeTimelineItemType, challengeIndex) => (
            <Sortable
              key={`${challenge.challengeId}-${challengeIndex}`}
              item={{
                type: 'challenge',
                id: challenge.challengeId,
                index: challengeIndex,
                focusAreaIndex: -1,
              }}
              onHover={(draggedItem) =>
                moveToChallenges(-1)(draggedItem, {
                  id: challenge.challengeId,
                  focusAreaIndex: -1,
                })
              }
              onDrop={({ id, index }) =>
                mutateChallenges({
                  customChallengeId: id,
                  index,
                })
              }
            >
              <GoalChallengeCard challenge={challenge}>
                <GoalBreakdownChallengeCardMenu challenge={challenge} />
              </GoalChallengeCard>
            </Sortable>
          )
        )}
      </VStack>
    </Droppable>
  );
};
